import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { applyInfo, festivalInfo } from "@/mocks/festival";

const FORM_ENDPOINT = "https://readdy.ai/api/form/dan70rlvmcl80jn297jg";

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200 transition-colors";
const labelClass = "mb-2 block text-sm text-foreground-800";

type Status = "idle" | "submitting" | "success" | "error";

export default function ApplyForm() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const guardValue = String(formData.get("contact_alt") ?? "").trim();
    if (guardValue) {
      setFormError("");
      setStatus("success");
      form.reset();
      return;
    }

    setStatus("submitting");
    setFormError("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "contact_alt") return;
      const text = value.toString();
      if (text.trim() === "") return;
      payload.append(key, text);
    });

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: unknown = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const result = parsed as
        | { code?: string; message?: string; meta?: { message?: string; detail?: string } }
        | null;
      const code = result?.code;
      const serverMsg =
        result?.meta?.message || result?.meta?.detail || result?.message || responseText || "";

      if (response.ok && code === "OK") {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "신청 접수에 실패했습니다. 잠시 후 다시 시도해 주세요.");
      }
    } catch {
      setStatus("error");
      setFormError("네트워크 오류로 신청에 실패했습니다. 잠시 후 다시 시도해 주세요.");
    }
  };

  if (status === "success") {
    return (
      <section id="form" className="w-full bg-background-100 px-4 md:px-8 py-16 md:py-24">
        <div className="max-w-2xl mx-auto rounded-lg border border-background-200 bg-background-50 px-6 py-12 md:px-10 md:py-16 text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-accent-500 text-white text-3xl">
            <i className="ri-check-line"></i>
          </span>
          <h2 className="mt-6 font-heading text-2xl md:text-3xl text-foreground-950">신청이 접수되었습니다</h2>
          <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
            거봉포도 수확체험 신청이 정상적으로 접수되었습니다.
            <br />
            신청하신 회차에 맞춰 축제 당일 현장 안내 데스크에서 확인해 주세요.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={() => setStatus("idle")}
              className="cursor-pointer whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-6 py-3 text-sm text-foreground-800 hover:bg-background-100 transition-colors"
            >
              추가 신청하기
            </button>
            <button
              type="button"
              onClick={() => navigate("/")}
              className="cursor-pointer whitespace-nowrap rounded-full btn-gradient px-6 py-3 text-sm font-medium text-white"
            >
              축제 홈으로
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="form" className="w-full bg-background-100 px-4 md:px-8 py-16 md:py-24">
      <div className="max-w-3xl mx-auto">
        <div className="reveal mb-8 md:mb-10 text-center">
          <span className="text-xs tracking-[0.24em] text-primary-600">APPLICATION FORM</span>
          <h2 className="mt-4 font-heading text-3xl md:text-4xl leading-tight text-foreground-950">
            수확체험 신청서 작성
          </h2>
          <p className="mt-4 text-sm md:text-base text-foreground-600">
            * 표시된 항목은 필수 입력 항목입니다.
          </p>
        </div>

        <form
          id="harvest-apply-form"
          data-readdy-form
          onSubmit={handleSubmit}
          className="reveal reveal-delay-2 rounded-lg border border-background-200 bg-background-50 px-5 py-7 md:px-9 md:py-10"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label htmlFor="apply-name" className={labelClass}>
                신청자 이름 <span className="text-primary-600">*</span>
              </label>
              <input
                id="apply-name"
                name="name"
                type="text"
                required
                maxLength={30}
                autoComplete="name"
                placeholder="이름을 입력해 주세요"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="apply-phone" className={labelClass}>
                연락처 <span className="text-primary-600">*</span>
              </label>
              <input
                id="apply-phone"
                name="phone"
                type="tel"
                required
                maxLength={20}
                autoComplete="tel"
                placeholder="010-0000-0000"
                className={inputClass}
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="apply-email" className={labelClass}>
                이메일 <span className="text-primary-600">*</span>
              </label>
              <input
                id="apply-email"
                name="email"
                type="email"
                required
                maxLength={60}
                autoComplete="email"
                placeholder="example@email.com"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="apply-date" className={labelClass}>
                방문 일자 <span className="text-primary-600">*</span>
              </label>
              <select id="apply-date" name="visit_date" required defaultValue="" className={inputClass}>
                <option value="" disabled>
                  방문 일자를 선택해 주세요
                </option>
                <option value="2026-09-20 (일)">2026. 09. 20 (일)</option>
                <option value="2026-09-21 (월)">2026. 09. 21 (월)</option>
              </select>
            </div>
            <div>
              <label htmlFor="apply-round" className={labelClass}>
                체험 회차 <span className="text-primary-600">*</span>
              </label>
              <select id="apply-round" name="round" required defaultValue="" className={inputClass}>
                <option value="" disabled>
                  체험 회차를 선택해 주세요
                </option>
                {applyInfo.rounds.map((round) => (
                  <option key={round} value={round}>
                    {round} 회차
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="apply-people" className={labelClass}>
                참가 인원 <span className="text-primary-600">*</span>
              </label>
              <select id="apply-people" name="people" required defaultValue="" className={inputClass}>
                <option value="" disabled>
                  인원을 선택해 주세요
                </option>
                {["1명", "2명", "3명", "4명", "5명 이상"].map((count) => (
                  <option key={count} value={count}>
                    {count}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="apply-group" className={labelClass}>
                신청 구분
              </label>
              <select id="apply-group" name="group_type" defaultValue="개인" className={inputClass}>
                <option value="개인">개인</option>
                <option value="가족">가족</option>
                <option value="단체">단체</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label htmlFor="apply-message" className={labelClass}>
                문의 사항
              </label>
              <textarea
                id="apply-message"
                name="message"
                rows={4}
                maxLength={500}
                placeholder="문의하실 내용을 500자 이내로 입력해 주세요 (선택)"
                className={`${inputClass} resize-none`}
              ></textarea>
            </div>
          </div>

          <input
            type="text"
            name="contact_alt"
            className="field-cloaked"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
          />

          <div className="mt-7 flex items-start gap-3 rounded-md bg-background-100 px-4 py-4">
            <input
              id="apply-agree"
              name="agree"
              type="checkbox"
              required
              value="동의함"
              className="mt-0.5 h-4 w-4 shrink-0 accent-primary-500 cursor-pointer"
            />
            <label htmlFor="apply-agree" className="cursor-pointer text-xs leading-relaxed text-foreground-600">
              개인정보 수집 및 이용에 동의합니다. (수집 항목: 이름·연락처·이메일 / 이용 목적: 수확체험 접수 및
              안내 / 보유 기간: 축제 종료 후 1개월)
            </label>
          </div>

          {status === "error" && formError && (
            <p className="mt-5 rounded-md bg-primary-50 border border-primary-200 px-4 py-3 text-sm text-primary-800">
              {formError}
            </p>
          )}

          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <button
              type="submit"
              disabled={status === "submitting"}
              className="cursor-pointer whitespace-nowrap rounded-full btn-gradient px-7 py-3.5 text-sm md:text-base font-medium text-white disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {status === "submitting" ? "접수 중..." : "수확체험 신청하기"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/")}
              className="cursor-pointer whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-7 py-3.5 text-sm md:text-base text-foreground-800 hover:bg-background-100 transition-colors"
            >
              축제 홈으로
            </button>
          </div>

          <p className="mt-5 text-xs leading-relaxed text-foreground-500">
            {festivalInfo.fullName} · {festivalInfo.dateText} · {festivalInfo.venue}
          </p>
        </form>
      </div>
    </section>
  );
}